/**
 * Spawn placement for encounter groups (plan M2-13): every ship `spawnDistance` [min, max] from the player,
 * outside the asteroid-field envelope and at least `stationExclusion` off every station's hull (scale SC-5e1: from the
 * bounding sphere, `|p − centre| − R`, so a km-scale station's exclusion starts at its hull), facing the player.
 * Candidates are seeded draws (uniform over the sphere of directions), retried deterministically; the player's
 * forward biases the first attempts into the forward hemisphere so groups tend to arrive where the player looks.
 * Allocation-free (D-014): module scratch only; positions are float64 world metres (D-011).
 */
import { lookRotation } from '../ai/steering'
import type { InsideFieldFn, SpawnRequests } from '../ai/waves'
import { logger } from '../core/logger'
import type { Quat, Vec3 } from '../core/math'
import { nextFloat, range, type Rng } from '../core/rng'
import type { EncountersConfig } from '../data/m2Schemas'

/** Attempts per ship restricted to the player's forward hemisphere before any direction is allowed. */
const FORWARD_ATTEMPTS = 24
/** Total candidate positions tried per ship before the last one is used anyway. */
const MAX_ATTEMPTS = 64

/** Anything with a float64 world position and a bounding radius (`StationRuntime` fits; radius 0 = from the centre). */
export interface PlacementStation {
  readonly pos: Vec3
  /** The station's gameplay radius R (m): the exclusion is measured from this sphere (scale SC-5e1). */
  readonly radius: number
}

// Module scratch (D-014).
const rot: Quat = { x: 0, y: 0, z: 0, w: 1 }

/** True when (x, y, z) is at least `minDist` off every station's hull (R + `minDist` from its centre). */
function clearOfStations(x: number, y: number, z: number, stations: readonly PlacementStation[], minDist: number): boolean {
  for (let i = 0; i < stations.length; i++) {
    const st = stations[i]
    const p = st.pos
    const dx = x - p.x
    const dy = y - p.y
    const dz = z - p.z
    const min = st.radius + minDist
    if (dx * dx + dy * dy + dz * dz < min * min) return false
  }
  return true
}

/**
 * Writes `count` (capped at `out.capacity`) spawn positions and rotations into `out` (`x/y/z`, `qx..qw`; local
 * −Z faces the player) and sets `out.count`; `defIndex`/`profile` are left as `composition` wrote them.
 * Returns the number of ships whose position satisfied every constraint (the rest use their last candidate
 * and log a warning).
 */
export function spawnPositions(
  playerPos: Vec3,
  playerFwd: Vec3,
  count: number,
  cfg: EncountersConfig,
  stations: readonly PlacementStation[],
  insideField: InsideFieldFn,
  rng: Rng,
  out: SpawnRequests,
): number {
  const n = Math.min(count, out.capacity)
  const fl = Math.sqrt(playerFwd.x * playerFwd.x + playerFwd.y * playerFwd.y + playerFwd.z * playerFwd.z)
  const fx = fl > 1e-9 ? playerFwd.x / fl : 0
  const fy = fl > 1e-9 ? playerFwd.y / fl : 0
  const fz = fl > 1e-9 ? playerFwd.z / fl : -1
  let good = 0
  for (let k = 0; k < n; k++) {
    let x = playerPos.x
    let y = playerPos.y
    let z = playerPos.z
    let placed = false
    for (let attempt = 0; attempt < MAX_ATTEMPTS && !placed; attempt++) {
      // Uniform direction on the unit sphere.
      const cz = nextFloat(rng) * 2 - 1
      const s = Math.sqrt(Math.max(0, 1 - cz * cz))
      const phi = range(rng, 0, Math.PI * 2)
      let dx = Math.cos(phi) * s
      let dy = Math.sin(phi) * s
      let dz = cz
      const along = dx * fx + dy * fy + dz * fz
      if (attempt < FORWARD_ATTEMPTS && along < 0) {
        // Mirror into the forward hemisphere.
        dx -= 2 * along * fx
        dy -= 2 * along * fy
        dz -= 2 * along * fz
      }
      const d = range(rng, cfg.spawnDistance[0], cfg.spawnDistance[1])
      x = playerPos.x + dx * d
      y = playerPos.y + dy * d
      z = playerPos.z + dz * d
      placed = !insideField(x, y, z) && clearOfStations(x, y, z, stations, cfg.stationExclusion)
    }
    if (placed) good++
    else logger.warn(`Encounter spawn: no position clear of the field and stations after ${MAX_ATTEMPTS} tries; using the last one`)
    out.x[k] = x
    out.y[k] = y
    out.z[k] = z
    lookRotation(playerPos.x - x, playerPos.y - y, playerPos.z - z, rot)
    out.qx[k] = rot.x
    out.qy[k] = rot.y
    out.qz[k] = rot.z
    out.qw[k] = rot.w
  }
  out.count = n
  return good
}
